/**
 * @file: useBubbleMenu.ts
 *
 * The long-press bubble action-menu overlay — one responsibility. A bubble
 * measures itself on long-press and `present`s its on-screen rect + a clone of
 * its body; the overlay host lifts the clone over a blurred backdrop and drops
 * the action menu beside it. State is a subject so only the overlay re-renders.
 *
 * Why an overlay (not the shared `HoldItem`): the message list is an INVERTED
 * FlatList (`scaleY(-1)` on the list + each cell), and a menu primitive that
 * measures a view *inside* a flipped cell mis-positions. Here the bubble does
 * its own `measureInWindow` (window coords) and the host renders inside a
 * full-window Modal, so the flip never sits between the overlay and the window.
 */

import { ChatMsg } from '@euler/model/chat';
import { useCreation } from '@euler/utils/hooks';
import { ReactNode, useCallback, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

/** The scale a bubble shrinks to while pressed; the lifted clone restores from
 * it to 1 as the menu opens (so the press-down flows into the lift). */
export const BUBBLE_PRESS_SCALE = 0.96;

/** A bubble's on-screen geometry (window coords) for anchoring the overlay. */
export interface BubbleRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The open menu: the pressed message, its rect, and a clone of its body. */
export interface BubbleMenuTarget {
  message: ChatMsg;
  isMine: boolean;
  rect: BubbleRect;
  /**
   * The width the source text actually wrapped at — the bubble's column (its
   * `maxWidth: 80%` box), which is WIDER than the content-shrunk bubble. The
   * clone wraps at this (as `maxWidth`) so it breaks line-for-line with the
   * source instead of re-wrapping narrower at the bubble's own width.
   */
  maxWidth: number;
  /** A render of the bubble body, lifted over the backdrop at `rect`. */
  clone: ReactNode;
}

export interface BubbleMenu {
  /** The open menu, or null when closed. */
  state$: BehaviorSubject<BubbleMenuTarget | null>;
  present: (target: BubbleMenuTarget) => void;
  dismiss: () => void;
}

export function useBubbleMenu(): BubbleMenu {
  const state$ = useCreation(
    () => new BehaviorSubject<BubbleMenuTarget | null>(null),
    [],
  );
  const present = useCallback(
    (target: BubbleMenuTarget) => state$.next(target),
    [state$],
  );
  const dismiss = useCallback(() => state$.next(null), [state$]);
  return useMemo(
    () => ({ state$, present, dismiss }),
    [state$, present, dismiss],
  );
}
